// Buyer Assistant floating widget. Advisory-only. Never claims certainty.

import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { askBuyerAssistant } from "@/lib/buyer-assistant.functions";

export function BuyerAssistantWidget() {
  const ask = useServerFn(askBuyerAssistant);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [disclaimer, setDisclaimer] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!q.trim()) return;
    setLoading(true);
    setAnswer(null);
    try {
      const res = await ask({ data: { question: q } });
      setAnswer(res.answer);
      setDisclaimer(res.disclaimer);
    } catch (e) {
      setAnswer(e instanceof Error ? e.message : "Assistant unavailable");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-4 right-4 z-40 rounded-full bg-gold text-black w-12 h-12 shadow-lg flex items-center justify-center"
        aria-label="Ask buyer assistant"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>
      {open && (
        <div className="fixed bottom-20 right-4 z-40 w-[min(360px,calc(100vw-2rem))] rounded-xl border border-border/60 bg-background shadow-xl p-4 space-y-3">
          <div className="text-sm font-medium">Buyer assistant</div>
          <textarea
            className="w-full text-sm rounded-md border border-border/60 bg-surface-2/40 p-2"
            rows={3}
            placeholder="e.g. Which graded Charizards are ending soon?"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button size="sm" onClick={submit} disabled={loading || !q.trim()}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ask"}
          </Button>
          {answer && (
            <div className="text-xs whitespace-pre-wrap rounded-md border border-border/40 bg-surface-2/30 p-2 max-h-64 overflow-auto">
              {answer}
              {disclaimer && <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">{disclaimer}</div>}
            </div>
          )}
        </div>
      )}
    </>
  );
}
